import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { MusicalWorkLinkCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { PlatformLinkTypeCreate } from "../../../platformLinkType/components/create/PlatformLinkTypeCreate";
import { PlatformLinkTypeCreateStore } from "../../../platformLinkType/store";
import { FormControl } from "@mui/material";

export type MusicalWorkLinkCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  urlSelectedValue?: { displayId: string; id: string } | null;
  onUrlChange?: (value: string) => void;
  url: string;
  urlError?: string;
  platformSelectedValue?: { displayId: string; id: string } | null;
  onPlatformSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onPlatformChange?: (value: string) => void;
  platformOptions?: { displayId: string; id: string }[];
  platformError?: string;
  typeSelectedValue?: { displayId: string; id: string } | null;
  onTypeSelected?: (value: { displayId: string; id: string } | null) => void;
  onTypeChange?: (value: string) => void;
  typeOptions?: { displayId: string; id: string }[];
  typeError?: string;
  musicalWorkLinkCreateStore: MusicalWorkLinkCreateStore;
};

export const MusicalWorkLinkCreateForm: React.FC<MusicalWorkLinkCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      url = "",
      urlError,
      onUrlChange = () => {},
      onPlatformSelected = () => {},
      platformSelectedValue = null,
      platformOptions = [],
      platformError,
      onPlatformChange = () => {},
      onTypeSelected = () => {},
      typeSelectedValue = null,
      typeOptions = [],
      typeError,
      onTypeChange = () => {},
      musicalWorkLinkCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="MusicalWorkLink"
              mode="create"
              values={{}}
              options={{ platform: platformOptions, type: typeOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "url":
                    onUrlChange?.(valor);
                    break;
                  case "platform":
                    onPlatformSelected?.(valor);
                    break;
                  case "type":
                    onTypeSelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={musicalWorkLinkCreateStore.getCardErrors("null")}
              title={langStore.groupMusicalWorkLinkLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={url}
                  error={Boolean(urlError)}
                  warning={
                    !urlError &&
                    Boolean(musicalWorkLinkCreateStore.warnings["url"])
                  }
                  helperText={
                    urlError ||
                    musicalWorkLinkCreateStore.warnings["url"]?.message
                  }
                  label={langStore.MusicalWorkLinkUrlLabel}
                  onChange={(value: string) => onUrlChange(value)}
                  description={langStore.MusicalWorkLinkUrlDescription}
                />
                <InputReference
                  id="platform"
                  label={langStore.MusicalWorkLinkPlatformLabel}
                  error={Boolean(platformError)}
                  helperText={
                    platformError ||
                    musicalWorkLinkCreateStore.warnings["platform"]?.message
                  }
                  selectedValue={platformSelectedValue}
                  onChange={(value: string) => onPlatformChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onPlatformSelected(value)
                  }
                  options={platformOptions}
                  description={langStore.MusicalWorkLinkPlatformDescription}
                  nullText={langStore.optionalFieldHint}
                />
                <InputReference
                  id="type"
                  label={langStore.MusicalWorkLinkTypeLabel}
                  error={Boolean(typeError)}
                  helperText={
                    typeError ||
                    musicalWorkLinkCreateStore.warnings["type"]?.message
                  }
                  selectedValue={typeSelectedValue}
                  onChange={(value: string) => onTypeChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onTypeSelected(value)
                  }
                  options={typeOptions}
                  description={langStore.MusicalWorkLinkTypeDescription}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "platformLinkType_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new PlatformLinkTypeCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <PlatformLinkTypeCreate
                          platformLinkTypeCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewPlatformLinkType"
                      cancelMethod="cancelNewPlatformLinkType"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
